import React, { useState, useMemo, useRef } from 'react';
import { Table as AppkitTable } from 'appkit-react';
import '../../scss/Table.scss';
import { InstituteNameSkeleton } from '../../../../common/Skeleton/SkeletonVariants';
import MultiKeySearchBar from '../MultiKeySearchBar';
import SortableHeader from '../SortableHeader';

const BenchmarkTable = ({
    data = [],
    institutionDetails = {},
    onExplainMdrm,
    selectedMdrms = [],
    searchKeys = [],
    onSearchKeysChange = () => { },
    benchmarkRSSDs,
    lockedInstitute,
}) => {
    const [showAbsDiff, setShowAbsDiff] = useState(false);
    const [clickedMdrm, setClickedMdrm] = useState(null);
    const [sorted, setSorted] = useState([]);
    const prevInstitutionListRef = useRef([]);

    const institutionList = useMemo(() => {
        let list = [];

        if (benchmarkRSSDs && benchmarkRSSDs.length > 0) {
            // Use benchmarkRSSDs directly
            list = [...benchmarkRSSDs];
        } else {
            const institutionSet = new Set();

            if (data && data.length > 0) {
                data.forEach((item) => {
                    if (item.institution_a?.rssid)
                        institutionSet.add(item.institution_a.rssid);
                    item.compare_with?.forEach((c) => {
                        if (c?.rssid) institutionSet.add(c.rssid);
                    });
                });
            }

            if (institutionDetails && Object.keys(institutionDetails).length > 0) {
                Object.keys(institutionDetails).forEach((key) => {
                    institutionSet.add(key);
                });
            }

            list = Array.from(institutionSet);
        }

        prevInstitutionListRef.current = list;
        return list;
    }, [data, institutionDetails, benchmarkRSSDs]);

    const reorderedInstitutionList = useMemo(() => {
        if (!lockedInstitute) return institutionList;
        return [
            lockedInstitute,
            ...institutionList.filter(rssd => rssd !== lockedInstitute),
        ];
    }, [institutionList, lockedInstitute]);

    const loweredKeys = (searchKeys || [])
        .map((k) => k.toLowerCase().trim())
        .filter(Boolean);

    const filteredRows = useMemo(() => {
        if (!data.length) return [];
        if (!loweredKeys.length) return data;

        return data.filter((row) => {
            const mdrm = (row.mdrm || '').toLowerCase();
            const schedule = (row.schedule || '').toLowerCase();
            const itemName = (row.item_name || '').toLowerCase();

            return loweredKeys.some(
                (key) =>
                    mdrm.includes(key) ||
                    schedule.includes(key) ||
                    itemName.includes(key),
            );
        });
    }, [data, loweredKeys]);

    const rows = useMemo(() => {
        if (!filteredRows.length) return [];

        const baseRows = filteredRows.map((row) => {
            const baseRow = {
                mdrm: row.mdrm,
                schedule: row.schedule,
                lineItemCode: row.line_item_code || '-',
                itemName: row.item_name,
                absoluteDifferencePercent: row.absolute_difference_percent,
                differencePercent: row.difference_percent,
            };

            if (row.institution_a?.rssid) {
                baseRow[row.institution_a.rssid] = row.institution_a.value;
            }
            row.compare_with?.forEach((c) => {
                if (c?.rssid) baseRow[c.rssid] = c.value;
            });

            return baseRow;
        });

        if (sorted.length > 0) {
            const { id: sortColumn, desc } = sorted[0];

            baseRows.sort((a, b) => compareRows(a, b, sortColumn, desc));
        }

        return baseRows;
    }, [filteredRows, sorted]);

    function compareRows(a, b, sortColumn, desc) {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return desc ? bVal - aVal : aVal - bVal;
        }

        const aStr = aVal.toString().toLowerCase();
        const bStr = bVal.toString().toLowerCase();

        if (aStr < bStr) return desc ? 1 : -1;
        if (aStr > bStr) return desc ? -1 : 1;
        return 0;
    }

    const onExplainClick = (mdrm) => {
        setClickedMdrm(mdrm);
        onExplainMdrm?.(mdrm);
        setTimeout(() => setClickedMdrm(null), 800);
    };

    const getColorClass = (percent) => {
        if (percent === null || percent === undefined || isNaN(percent)) return '';
        const absVal = Math.abs(percent);
        if (absVal >= 100) return 'change-negative';
        if (absVal >= 50) return 'change-positive';
        return '';
    };

    const onSortedChange = (newSorted, column) => {
        setSorted(newSorted);
    };

    const columns = useMemo(() => {
        const baseCols = [
            {
                Header: '✨',
                id: 'explain',
                width: 30,
                sortable: false,
                Cell: ({ row }) => {
                    const mdrm = row.mdrm || '';
                    return (
                        <div
                            role="button"
                            tabIndex={0}
                            title="Explain MDRM"
                            onClick={() => onExplainClick(mdrm)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onExplainClick(mdrm);
                            }}
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                opacity: clickedMdrm === mdrm ? 1 : 0.6,
                                animation:
                                    clickedMdrm === mdrm
                                        ? 'float 1.5s ease-in-out infinite'
                                        : 'none',
                            }}
                            aria-label={`Explain ${mdrm}`}
                        >
                            ✨
                        </div>
                    );
                },
            },
            {
                id: 'mdrm',
                accessor: 'mdrm',
                sortable: false,
                width: 70,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label="MDRM"
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
            },
            {
                id: 'schedule',
                accessor: 'schedule',
                sortable: false,
                className: 'schedule-cell',
                width: 80,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label="Schedule"
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
            },
            {
                id: 'lineItemCode',
                accessor: 'lineItemCode',
                sortable: false,
                width: 55,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label={
                            <div className="multiline-headers">
                                Line
                                <br />
                                Item
                                <br />
                                Code
                            </div>
                        }
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
                Cell: ({ value }) => {
                    if (!value) return null;

                    const maxLength = 5;
                    const truncated = value.length > maxLength ? value.slice(0, maxLength) + '...' : value;

                    return (
                        <div className="markdown-cell" title={value}>
                            {truncated}
                        </div>
                    );
                },
            },
            {
                id: 'itemName',
                accessor: 'itemName',
                sortable: false,
                width: 130,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label="Item Name"
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
                Cell: ({ value }) => {
                    if (!value) return null;

                    return (
                        <div className="item-name-cell" title={value}>
                            {value}
                        </div>
                    );
                },
            },
            ...reorderedInstitutionList.map((q) => ({
                id: q,
                accessor: q,
                sortable: false,
                width: 100,
                Header: ({ column }) => {
                    const fullText = institutionDetails[q];

                    if (!fullText) return <InstituteNameSkeleton />;

                    const maxChars = 5;
                    const nameParts = fullText.split(' ');

                    const truncateWord = (word) =>
                        word.length > maxChars ? word.slice(0, maxChars) + '...' : word;

                    return (
                        <SortableHeader
                            column={column}
                            label={
                                <div
                                    className={`institution-label ${q === lockedInstitute ? 'locked-institution' : ''
                                        }`}
                                >
                                    {nameParts.map((part, i) => (
                                        <div
                                            key={i}
                                            title={part}
                                            className="institution-name"
                                        >
                                            {truncateWord(part)}
                                        </div>
                                    ))}
                                </div>
                            }
                            sorted={sorted}
                            onSortedChange={onSortedChange}
                        />
                    );
                },
                Cell: ({ value }) =>
                    value !== undefined ? Number(value).toLocaleString() : '-',
            })),
        ];

        baseCols.push({
            id: 'differencePercent',
            accessor: 'differencePercent',
            sortable: false,
            width: 70,
            Header: ({ column }) => (
                <SortableHeader
                    column={column}
                    label={
                        <div className="multiline-headers">
                            Diff
                            <br />
                            (%)
                        </div>
                    }
                    sorted={sorted}
                    onSortedChange={onSortedChange}
                />
            ),
            Cell: ({ value }) => (
                <div className={getColorClass(value)}>
                    {Number.isFinite(value) ? `${value.toFixed(2)}%` : 'N/A'}
                </div>
            ),
        });

        if (showAbsDiff) {
            baseCols.push({
                id: 'absoluteDifferencePercent',
                accessor: 'absoluteDifferencePercent',
                sortable: false,
                width: 70,
                Header: ({ column }) => (
                    <SortableHeader
                        column={column}
                        label={
                            <div className="multiline-headers">
                                Abs
                                <br />
                                Diff
                                <br />
                                (%)
                            </div>
                        }
                        sorted={sorted}
                        onSortedChange={onSortedChange}
                    />
                ),
                Cell: ({ value }) => (
                    <div className={getColorClass(value)}>
                        {Number.isFinite(value) ? `${value.toFixed(2)}%` : 'N/A'}
                    </div>
                ),
            });
        }

        return baseCols;
    }, [institutionList, institutionDetails, showAbsDiff, clickedMdrm, sorted]);

    return (
        <div className="table-demo">
            <div className="table-demo-wrapper">
                <div className="table-header">
                    <label>
                        <input
                            type="checkbox"
                            checked={showAbsDiff}
                            onChange={() => setShowAbsDiff((prev) => !prev)}
                            aria-label="Toggle showing absolute difference percentage column"
                        />
                        Show Abs Diff (%)
                    </label>

                    <div className="searchbar-container">
                        <MultiKeySearchBar
                            onKeysChange={onSearchKeysChange}
                            placeholder="Search MDRM, Schedule, Item Name..."
                            searchKeys={searchKeys}
                        />
                    </div>
                </div>

                <AppkitTable
                    data={rows}
                    columns={columns}
                    sortable={false}
                    defaultPageSize={10}
                    sorted={sorted}
                    onSortedChange={onSortedChange}
                    showPageSizeOptions={false}
                    size="sm"
                    rowClassName={({ row }) =>
                        selectedMdrms.includes(row.mdrm) ? 'selected-row' : ''
                    }
                    aria-label="Benchmark Table"
                />
            </div>
        </div>
    );
};

export default BenchmarkTable;
